import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="privacy-container">
      <div className="privacy-content">
        <h1>Privacy Policy</h1>

        <section>
          <h2>1. Introduction</h2>
          <p>
            CypressEra ("CypressEra," "we," "us," or "our") is committed to protecting your privacy. This 
            Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use 
            our power system analysis software and related services (collectively, the "Services"). Please read 
            this Privacy Policy carefully. By using the Services, you consent to the practices described in 
            this Privacy Policy.
          </p>
          <p>
            CypressEra operates from Vancouver, British Columbia, Canada. We are the data controller
            responsible for the processing of your personal information as described in this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information You Provide</h3>
          <p>We collect information that you voluntarily provide when using our Services, including:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, company name, job title, phone number, 
            and billing information when you register for an account</li>
            <li><strong>Communications:</strong> Information you provide when you contact us, including support 
            requests, feedback, and inquiries</li>
            <li><strong>Payment Information:</strong> Credit card numbers, billing address, and transaction history</li>
            <li><strong>User Data:</strong> Power system models, analysis configurations, and data files you upload 
            to the Services</li>
          </ul>
          
          <h3>2.2 Information Collected Automatically</h3>
          <p>When you access our Services, we automatically collect certain information, including:</p>
          <ul>
            <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, 
            browser type, and browser language</li>
            <li><strong>Log Data:</strong> IP address, access times, pages viewed, URLs visited, referring URL, 
            and other diagnostic data</li>
            <li><strong>Usage Data:</strong> Features used, analysis performed, time spent using the Services, 
            and interaction patterns</li>
            <li><strong>Cookies and Tracking Technologies:</strong> Information collected through cookies, web 
            beacons, and similar technologies (see Section 8 for details)</li>
          </ul>
          
          <h3>2.3 Information from Third Parties</h3>
          <p>We may receive information about you from third parties, including:</p>
          <ul>
            <li>Identity verification services</li>
            <li>Payment processors</li>
            <li>Social media platforms (if you connect your account)</li>
            <li>Business partners and affiliates</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for various purposes, including:</p>
          
          <h3>3.1 Service Delivery</h3>
          <ul>
            <li>Providing, maintaining, and improving our Services</li>
            <li>Processing transactions and sending related information</li>
            <li>Enabling features and functionality within the Services</li>
            <li>Performing power system analysis and generating results</li>
            <li>Storing and managing your User Data</li>
          </ul>
          
          <h3>3.2 Communication</h3>
          <ul>
            <li>Sending service-related communications, including updates and security alerts</li>
            <li>Responding to your comments, questions, and support requests</li>
            <li>Sending marketing communications (with your consent where required)</li>
          </ul>
          
          <h3>3.3 Security and Legal Compliance</h3>
          <ul>
            <li>Detecting, preventing, and addressing technical issues and security threats</li>
            <li>Protecting against fraudulent, unauthorized, or illegal activity</li>
            <li>Complying with applicable laws, regulations, and legal processes</li>
            <li>Enforcing our agreements and policies</li>
          </ul>
          
          <h3>3.4 Business Improvement</h3>
          <ul>
            <li>Analyzing usage patterns to improve our Services</li>
            <li>Developing new features, products, and services</li>
            <li>Conducting research and analytics</li>
          </ul>
        </section>

        <section>
          <h2>4. Legal Basis for Processing (GDPR)</h2>
          <p>
            For individuals in the European Economic Area (EEA) and United Kingdom, we process personal information 
            based on the following legal grounds:
          </p>
          <ul>
            <li><strong>Contract Performance:</strong> Processing necessary to provide the Services under our 
            agreement with you</li>
            <li><strong>Legitimate Interests:</strong> Processing for our legitimate business interests, such as 
            improving our Services and ensuring security</li>
            <li><strong>Consent:</strong> Processing based on your consent for specific purposes, such as marketing 
            communications</li>
            <li><strong>Legal Obligation:</strong> Processing necessary to comply with applicable laws and regulations</li>
          </ul>
        </section>

        <section>
          <h2>5. How We Share Your Information</h2>
          <p>We may share your information with:</p>
          
          <h3>5.1 Service Providers</h3>
          <p>
            We share information with third-party service providers who perform services on our behalf, including:
          </p>
          <ul>
            <li>Cloud computing and hosting providers</li>
            <li>Payment processors</li>
            <li>Customer support platforms</li>
            <li>Email and communication services</li>
            <li>Analytics providers</li>
          </ul>
          <p>
            These service providers are contractually obligated to protect your information and may only use it 
            to provide services to us.
          </p>
          
          <h3>5.2 Business Transfers</h3>
          <p>
            In connection with a merger, acquisition, financing, reorganization, or sale of assets, your 
            information may be transferred to the acquiring entity.
          </p>
          
          <h3>5.3 Legal Requirements</h3>
          <p>We may disclose information if required by law, regulation, or legal process, including:</p>
          <ul>
            <li>Response to subpoenas, court orders, or government requests</li>
            <li>Protection of our rights, privacy, safety, or property</li>
            <li>Enforcement of our agreements and policies</li>
            <li>Cooperation with law enforcement agencies</li>
          </ul>
          
          <h3>5.4 With Your Consent</h3>
          <p>
            We may share your information with third parties when you provide explicit consent for such sharing.
          </p>
          
          <h3>5.5 Aggregated and De-identified Data</h3>
          <p>
            We may share aggregated or de-identified information that cannot reasonably be used to identify you.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes for which it 
            was collected, including:
          </p>
          <ul>
            <li>Providing the Services to you</li>
            <li>Complying with legal obligations</li>
            <li>Resolving disputes</li>
            <li>Enforcing our agreements</li>
          </ul>
          <p>
            When your account is terminated, we will delete or anonymize your personal information within a 
            reasonable period, unless retention is required by law. User Data may be deleted immediately upon 
            account termination unless you request otherwise.
          </p>
        </section>

        <section>
          <h2>7. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information 
            against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Regular security assessments and audits</li>
            <li>Employee training and security policies</li>
            <li>Incident response procedures</li>
          </ul>
          <p>
            However, no method of transmission over the Internet or electronic storage is completely secure. 
            While we strive to use commercially acceptable means to protect your information, we cannot guarantee 
            absolute security.
          </p>
        </section>

        <section>
          <h2>8. Cookies and Tracking Technologies</h2>
          <p>We use cookies and similar tracking technologies to:</p>
          <ul>
            <li>Remember your preferences and settings</li>
            <li>Understand how you use our Services</li>
            <li>Improve our Services and user experience</li>
            <li>Provide personalized content</li>
          </ul>
          
          <h3>8.1 Types of Cookies We Use</h3>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for the Services to function properly</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
            <li><strong>Analytics Cookies:</strong> Collect information about how you use our Services</li>
            <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
          </ul>
          
          <h3>8.2 Managing Cookies</h3>
          <p>
            You can control cookies through your browser settings. However, disabling cookies may limit certain 
            features of our Services. For more information, see our Cookie Policy.
          </p>
        </section>

        <section>
          <h2>9. Your Privacy Rights</h2>
          
          <h3>9.1 Rights Under PIPEDA (Canada)</h3>
          <p>Under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA), you have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Request correction of inaccurate information</li>
            <li>Withdraw consent for processing</li>
            <li>File a complaint with the Office of the Privacy Commissioner of Canada</li>
          </ul>
          
          <h3>9.2 Rights Under GDPR (EEA and UK)</h3>
          <p>If you are located in the EEA or UK, you have additional rights, including:</p>
          <ul>
            <li><strong>Right to Access:</strong> Obtain a copy of your personal information</li>
            <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete information</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your personal information</li>
            <li><strong>Right to Restriction:</strong> Limit processing of your information</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
            <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
          </ul>
          
          <h3>9.3 California Privacy Rights (CCPA)</h3>
          <p>If you are a California resident, you have the right to:</p>
          <ul>
            <li>Know what personal information is collected and how it is used</li>
            <li>Request deletion of personal information</li>
            <li>Opt-out of the sale of personal information (we do not sell personal information)</li>
            <li>Non-discrimination for exercising your privacy rights</li>
          </ul>
          
          <h3>9.4 Exercising Your Rights</h3>
          <p>
            To exercise any of these rights, please contact us at privacy@cypressera.com. We will respond to 
            your request within 30 days. We may require verification of your identity before processing your request.
          </p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country of residence, 
            including Canada and the United States. These countries may have different data protection laws.
          </p>
          <p>
            When transferring personal information from the EEA, UK, or other jurisdictions with data protection 
            laws, we implement appropriate safeguards, including:
          </p>
          <ul>
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Other legally valid transfer mechanisms</li>
          </ul>
        </section>

        <section>
          <h2>11. Children's Privacy</h2>
          <p>
            Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal 
            information from children. If we discover that we have collected personal information from a child under 
            18, we will promptly delete such information. If you believe we have collected information from a child, 
            please contact us at privacy@cypressera.com.
          </p>
        </section>

        <section>
          <h2>12. Third-Party Links</h2>
          <p>
            Our Services may contain links to third-party websites or services. We are not responsible for the 
            privacy practices of these third parties. We encourage you to read the privacy policies of any 
            third-party websites you visit.
          </p>
        </section>

        <section>
          <h2>13. Data Breach Notification</h2>
          <p>
            In the event of a data breach that poses a significant risk to your rights and freedoms, we will 
            notify you and the relevant supervisory authority within 72 hours of becoming aware of the breach, 
            as required by applicable law. Notification will include:
          </p>
          <ul>
            <li>Nature of the breach</li>
            <li>Categories and approximate number of individuals affected</li>
            <li>Likely consequences</li>
            <li>Measures taken to address the breach</li>
          </ul>
        </section>

        <section>
          <h2>14. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes by:
          </p>
          <ul>
            <li>Posting the updated Privacy Policy on our website</li>
            <li>Sending you an email notification</li>
            <li>Providing in-app notification</li>
          </ul>
          <p>
            The "Last Updated" date at the top of this Privacy Policy indicates when the most recent changes 
            were made. We encourage you to review this Privacy Policy periodically.
          </p>
        </section>

        <section>
          <h2>15. Contact Us</h2>
          <h3>15.1 Regulatory Authorities</h3>
          <p>
            If you believe we have not adequately addressed your privacy concerns, you have the right to lodge 
            a complaint with a supervisory authority:
          </p>
          <ul>
            <li><strong>Canada:</strong> Office of the Privacy Commissioner of Canada (www.priv.gc.ca)</li>
            <li><strong>EEA:</strong> The data protection authority in your country of residence</li>
            <li><strong>UK:</strong> Information Commissioner's Office (ico.org.uk)</li>
          </ul>
        </section>

        <section>
          <h2>16. Additional Provisions for Specific Jurisdictions</h2>
          
          <h3>16.1 European Economic Area and United Kingdom</h3>
          <p>
            For users in the EEA and UK, CypressEra has appointed a representative in the European Union 
            pursuant to Article 27 of the GDPR. Contact details are available upon request.
          </p>
          
          <h3>16.2 California Residents</h3>
          <p>
            Under the California Consumer Privacy Act (CCPA), California residents have additional rights. 
            We do not sell personal information and have not sold personal information in the preceding 12 months. 
            For the past 12 months, we have disclosed the following categories of personal information for 
            business purposes: identifiers, commercial information, and internet activity.
          </p>
          
          <h3>16.3 Australian Residents</h3>
          <p>
            For users in Australia, we comply with the Australian Privacy Principles (APPs) under the Privacy 
            Act 1988 (Cth). You may contact us to access or correct your personal information.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
